/* The sketcher shell: toolbars, the constraint list, the diagnosis banner and the dialogs.
 *
 *   tools     P point · L line · R rectangle · C circle · A arc (centre, start, end)
 *             3 arc through two ends and a point on it
 *   Esc       stop a DOF animation, then drop the tool's pending points, then put the tool
 *             down — Select is not a tool, it is what no tool being held looks like, so it
 *             is also where clicking the pressed toolbar button leaves you
 *   select    the pointer lights what a click would take — a figure or point, a solid's edge,
 *             else its face — and a click takes it (shift = multi), or drag a box over empty
 *             canvas to take everything that lies entirely inside it.  ⌘-click (Ctrl off a
 *             Mac) lists everything under the pointer — what is drawn, the edges and faces
 *             behind one another, the bodies — to choose the one meant
 *   constrain I coincident · D dimension — length, run, rise, radius, offset, ring, or a
 *             corner's angle
 *             H horizontal · V vertical · B parallel · ⇧L perpendicular · ⇧M midpoint
 *             E equal · T tangent · ⇧Q symmetric
 *   dimension D states one at once and opens its number where it will be read: move it where
 *             you want it and click to plant it, type, Enter — Esc takes the whole thing back.
 *             The drawing holds still while you carry it: nothing is solved until it lands.
 *             On two points, where you put it is *which* dimension it is: above or below them
 *             the run between them, out to either side the rise, across them their length.
 *             Every dimension is called out on the drawing: click one to select it, drag it
 *             where you want it, double-click it to change its number.  Edit ▸ Re-place
 *             dimensions undoes the arranging; Options turns the lot off.  A number may be an
 *             expression over the document's numbers — `w / 2`, `sin(h * 10)` — and the core
 *             evaluates them (Solution ▸ Diagnose lists them).  Typing a number over one that
 *             reads a `param` changes the param.  An unknown is declared, `param a: Length`:
 *             `a` on two dimensions ties them to each other and leaves what they are worth to
 *             the solver
 *   editing   F fix/unfix · G construction · Del delete · Ctrl+Z undo · ⇧Ctrl+Z redo ·
 *             Ctrl+X/C/V cut, copy, paste the selection
 *   workspace one scene in space: every sketch stands on its own plane and solids are drawn
 *             under them.  Right-drag orbits, middle-drag (or ⇧ right-drag) pans, the wheel
 *             zooms; a trackpad pinches to zoom and scrolls to pan, and two fingers on a touch
 *             screen pinch and pan together.  The chooser in the viewport's upper right is the plane the next thing is
 *             drawn on — std.front, std.side, std.top and every plane the document names — and
 *             choosing one turns the view square on to it.  Plane (N) and Axis (X) drop a datum
 *             constrained to the selection, and Insert ▸ Plane… names the plane first; J
 *             projects two points, one in each of two views, onto one point in space
 *   solids    U extrude and Q revolve the region under a click; M union, Z cut and Y intersect
 *             take the body to change, then the bodies to change it with — the box shows the
 *             result before it is written — and a click back on the first (or Enter) writes it.
 *             ⇧F fillet and ⇧C chamfer the edges selected, or clicked — a face for every edge
 *             of it, a body for every edge of the body — at the size typed
 *   menus     File/Edit/Insert/Solution/Help hold everything that is not a tool or a constraint;
 *             the solver's own switches are behind Solution ▸ Options
 *
 * Everything below is presentation; the solver, diagnosis, decomposition and root selection
 * all live in core/ and are shared with the test suite.  This file is the wiring: what is on
 * the bars, what the keys do, and what the view calls back into.  The rest of the shell is
 * next door — `shell` holds the page and the view, `commands` the constraints bar, `dialogs`
 * what the menus open, `lists` the constraints window and the status line, and `dimbox` the
 * number a dimension is edited in. */
import * as io from '../core/io.js';
import * as solids from '../core/mesh.js';
import { handed } from '../core/modules.js';
import type { ExportJob, ExportReply } from './export-worker.js';
import { drawingActive, exportDrawingSvg } from './drawing.js';
import { CONSTRAINT_BUTTONS } from './commands.js';
import {
  about, alternatives, doOpen, flipBranch, insertComponent, insertPlane, leaveExample, openCase,
  openExample, openGithub, openGithubAddress, options, report, reportSolve, showDiagnosis,
} from './dialogs.js';
import { closeChooser, onChoose, onContextMenu, onWays } from './choose.js';
import { editValue, onDimension, onExtent, onRound } from './dimbox.js';
import { bindProgramPanel, goToSource, onSide, openDrawing, openSide, refreshProgram,
  resetProgramFiles, saveProjectFile, showClaims, showStatementFor, toggleProgramPanel }
  from './program.js';
import { OutlinePane } from './outline.js';
import { closePanel, openPanel, refresh, refreshPanel, refreshStatus } from './lists.js';
import {
  aboutBadge, barConstraints, barTools, canvas, currentConstraint, focusConstraint, hooks, menubar,
  planeSelect, view, initialExample, initialGithub,
} from './shell.js';
import {
  MenuItem, ToolbarButton, addButton, addMenu, addTopic, closeMenus, download, fitBars,
  judgingLine, openImage, scenarioLine, toast,
} from './ui.js';
import { BuildBar, clock } from './build-bar.js';
import type { Judging } from './claim-judging.js';
import type { Scenario, ScenarioAnswer } from '../core/program.js';
import { bindScenarioCard, refreshScenarioCard } from './scenario-card.js';
import { Tool } from './view.js';
import type { ShownKind } from '../core/model.js';
import { Timeline } from './timeline.js';
import { SectionBar } from './section-bar.js';

/* -- toolbars ---------------------------------------------------------------- */

const toolButtons = new Map<Tool, HTMLButtonElement>();

/** The buttons follow the view, so Escape backing out of a tool updates them too.  Select is
 *  not a button: it is what the toolbar looks like with nothing pressed, so clicking the
 *  pressed tool puts it down and lands you back there. */
view.onTool = (t) => {
  for (const [k, b] of toolButtons) b.setAttribute('aria-pressed', String(k === t));
};
/* The element tools, then past a divider what the geometry you drew is *for*, as against the
 * constraints that hold it — all under one head, Elements, on a bar too narrow for them
 * (`fitBar`).  A table like the constraints bar's, so the chip and the accelerator stay one
 * string — see ACTION_KEYS. */
const ELEMENT_TOOLS = [
  ['Point', 'point', 'p'], ['Line', 'line', 'l'], ['Rect', 'rect', 'r'],
  ['Circle', 'circle', 'c'], ['Arc', 'arc', 'a'], ['Arc 3-pt', 'arc3', '3'],
  ['Spline', 'spline', 's'], ['Spline fit', 'splinefit', 'w'],
] as [string, Tool, string][];
/* What makes solids of the drawing, under its own head beside the elements: extrude and
 * revolve, then the Boolean operations — the body to change, then the bodies to change it with. */
const BOOLEAN = 'click the body to change, then each body to change it with; click the first '
  + 'again, or Enter, to write it.  ';
const EDGES = 'click its edges — a chain of them, open or closed — or a face for every edge of '
  + 'it, or select them first (a body selected is every edge of it); type the size and Enter to '
  + 'write it.  ⌘-click for an edge behind another.  A concave edge is filled, a convex one cut.  ';
const SOLID_TOOLS = [
  ['Extrude', 'extrude', 'u'], ['Revolve', 'revolve', 'q'],
  ['Union', 'union', 'm', `Join bodies: ${BOOLEAN}`],
  ['Cut', 'cut', 'z', `Cut a body with others: ${BOOLEAN}`],
  ['Intersect', 'common', 'y', `Keep what bodies share (common): ${BOOLEAN}`],
  ['Fillet', 'fillet', '⇧f', `Round edges of a solid (std's \`round\`): ${EDGES}`],
  ['Chamfer', 'chamfer', '⇧c', `Cut edges of a solid flat (std's \`chamfer\`): ${EDGES}`],
] as [string, Tool, string, string?][];
const TOOLS = [...ELEMENT_TOOLS, ...SOLID_TOOLS];
const toolButton = ([label, tool, key, what]: [string, Tool, string, string?]): ToolbarButton => ({
  label, key, toggle: true,
  title: `${what ?? ''}Click again to put the tool down and go back to selecting`,
  onClick: () => view.setTool(view.tool === tool ? 'select' : tool),
});
const TOOL_BUTTONS: ToolbarButton[] = [
  { label: 'Construction', key: 'g', onClick: () => view.toggleConstructionSelected(),
    title: 'Draw the selected lines/circles/arcs dashed as reference geometry (they still constrain)' },
  { label: 'Component', key: '⇧g', onClick: () => view.makeComponent(),
    title: 'Package the selection as a component of the document, drawn again as one instance of '
         + 'it: what it reads from the rest of the drawing becomes the component’s arguments '
         + '(Insert ▸ Component… names it)' },
  { label: 'Plane', key: 'n', onClick: () => view.addDatum('plane'),
    title: 'A plane, constrained to the selection: through a point · three points · a point, '
         + 'square to a line or axis · holding two lines or axes · parallel to a plane · '
         + 'with nothing selected, free, beside the plane being drawn on' },
  { label: 'Axis', key: 'x', onClick: () => view.addDatum('axis'),
    title: 'An axis, constrained to the selection: through a point · two points · along a line '
         + '· square to a plane, through a point or its origin · where two planes meet · with '
         + 'nothing selected, free, square to the plane being drawn on' },
];
/** A head over tools, and past a divider any other buttons; the tools' buttons are the ones
 *  `onTool` presses. */
const toolTopic = (label: string, tools: [string, Tool, string, string?][],
                   rest: ToolbarButton[] = []) =>
  addTopic(barTools, label, [...tools.map(toolButton), ...(rest.length ? [null, ...rest] : [])])
    .slice(0, tools.length).forEach((b, i) => toolButtons.set(tools[i][1], b));
toolTopic('Elements', ELEMENT_TOOLS, TOOL_BUTTONS);
toolTopic('Solids', SOLID_TOOLS, [
  { label: 'Sketch on face', key: 'k',
    onClick: () => {
      const [pick] = view.selectedSolids;
      if (pick) view.sketchOnFace(pick);
      else toast('select a face of a solid first — or double-click it');
    },
    title: 'Draw on the selected face of a solid: the plane lying there, or a plane written onto '
         + 'it that follows the solid (double-click a face does the same)' },
]);
/* The workspace's own button, in its corner: orbit mode, where one finger turns the view — shown
 * only to a finger (`app.css`), since a mouse orbits with its right button; `o` works anywhere. */
const VIEW_BUTTONS: ToolbarButton[] = [
  { label: 'Orbit', key: 'o', toggle: true,
    title: 'Orbit mode: a drag turns the workspace about, rather than drawing or selecting — one '
         + 'finger on a phone.  Click again to put it down',
    onClick: () => {
      view.setOrbitMode(!view.orbitMode);
      toast(view.orbitMode ? 'orbit: drag to turn the view' : 'orbit off');
    } },
];
const [orbitButton] = VIEW_BUTTONS.map((b) => addButton(
  document.getElementById('view-buttons') as HTMLElement, b));
/** The Orbit button follows the view, as the tools' do. */
view.onOrbitMode = (on) => orbitButton.setAttribute('aria-pressed', String(on));
view.setTool('select');
addTopic(barConstraints, 'Constraints', CONSTRAINT_BUTTONS);
fitBars(document.querySelector('header')!, [barTools, barConstraints]);

/* -- menu bar ------------------------------------------------------------------- */

/** The drawing as an SVG file.
 *
 *  **The core draws it.**  The figures, the strokes a style sheet resolves to and the whole of
 *  the arithmetic are `gcs_core::svg`'s, which is what keeps this button and `solventc --output`
 *  from being two implementations of one picture.  The app contributes the one thing the core
 *  cannot know: how wide the page is.  The canvas's own width is the honest answer — the export
 *  is the size of the area the drawing was being looked at in — and everything drawn at a
 *  constant size follows from it, since a page width is what fixes `unit`.  A canvas with no
 *  width yet is `svg::render`'s business and not this button's — it floors the page itself, and
 *  a second floor here would be a second rule about the same number.
 *
 *  The *camera* is deliberately not consulted.  An export is of the drawing, not of the view:
 *  the core fits the whole figure to the page, so a file is the same whatever the pan and zoom
 *  happened to be when the button was pressed. */
function exportSvg(): void {
  if (drawingActive()) { exportDrawingSvg(); return; }
  download('sketch.svg', io.svg(view.sketch, view.width));
  toast('exported sketch.svg');
}

/** **The object, as a file.**
 *
 *  Two formats because they answer two questions.  glTF carries what the kernel knows — every
 *  face of every object under the name the document gives it, and the normals that make a bore's
 *  wall shade round — and is what a viewer opens.  STL carries triangles and nothing else, and
 *  is what a printer takes.  Neither is a boundary representation, so neither is what a
 *  machinist's STEP would be; that is a different thing and is not this.
 *
 *  What is exported is the document's **objects** — the solids nothing else is made of.  A bore
 *  is a hole in a part, not a part beside it. */
async function exportSolid(kind: 'glb' | 'stl'): Promise<void> {
  const objs = solids.objects(view.sketch);
  if (objs.length === 0) {
    toast('this drawing has no solid — a `solid` statement makes one from a `face`');
    return;
  }
  const stem = objs.length === 1 ? objs[0].name.replace(/[^\w.-]/g, '_') : 'solids';
  // the core refuses a solid it cannot write — one still being built, above all — and says why;
  // unsaid, a menu item that does nothing reads as broken
  try { await exportFile(kind, objs, stem); }
  catch (e) { toast(`cannot export: ${e instanceof Error ? e.message : String(e)}`); }
}

async function exportFile(kind: 'glb' | 'stl', objs: { name: string; index: number }[], stem: string): Promise<void> {
  if (kind === 'glb') {
    // one scene holds every object, so glTF need not be told which part of an assembly to be
    download(`${stem}.glb`, solids.glb(view.sketch));
    toast(`exported ${stem}.glb — ${objs.length} object(s), every face named`);
    return;
  }
  // an STL is a triangle soup with no grouping, so several objects go in one file as one soup; an
  // object still being built is refused, by name, by the core
  const parts = objs.map((o) => solids.stl(view.sketch, o.index));
  const name = `${stem}.stl`;
  download(name, parts.length === 1 ? parts[0] : joinStl(parts));
  toast(`exported ${name}`);
}

/** **A solid's exact export, made in a worker** (`export-worker.ts`): the core's own kernel builds
 *  each object — a swept body of the generating class as one sector patterned — and writes its STEP
 *  parsed back against it, or its STL held to the fabrication tolerance and to its material field;
 *  the functions `solventc --step/--stl` calls, so the file is the one the terminal writes. It takes
 *  seconds, so the page goes on drawing, and says so; a refusal is the export's, its stage named. */
let exporter: Worker | null = null;
let exportJob = 0;
const exportsWaiting = new Map<number, { name: string; kind: 'step' | 'stl' }>();
function exactExport(kind: 'step' | 'stl'): void {
  const objs = solids.objects(view.sketch);
  if (objs.length === 0) {
    toast('this drawing has no solid — a `solid` statement makes one from a `face`');
    return;
  }
  if (!exporter) {
    const w = new Worker(new URL('./export-worker.bundle.js', import.meta.url), { type: 'module' });
    w.onmessage = (ev: MessageEvent<ExportReply>) => {
      const reply = ev.data;
      const asked = exportsWaiting.get(reply.id);
      if (!asked) return;
      exportsWaiting.delete(reply.id);
      if ('error' in reply) { toast(`cannot export ${asked.name}: ${reply.error}`); return; }
      download(`${asked.name}.${asked.kind}`, reply.bytes);
      toast(`exported ${asked.name}.${asked.kind} — by the core's kernel, its file checked`);
    };
    w.onerror = (ev) => { exportsWaiting.clear(); toast(`the export stopped: ${ev.message || 'its worker failed'}`); exporter = null; };
    exporter = w;
  }
  const text = view.source;
  const modules = handed(text);
  for (const o of objs) {
    const id = ++exportJob;
    const name = o.name.replace(/[^\w.-]/g, '_');
    exportsWaiting.set(id, { name, kind });
    const job: ExportJob = { id, text, modules, x: view.sketch.getX(), solid: o.index, kind, tolerance: 0.01 };
    exporter.postMessage(job);
  }
  toast(`exporting ${objs.length === 1 ? objs[0].name : `${objs.length} objects`} as ${kind.toUpperCase()} — a swept body takes seconds`);
}

/** **The claims about solids being judged, said in the footer** — the worker's word on where it
 *  stands, in the core's words (`judging claim a clear(1mm) b (2 of 3), pose 5 of 37`), a bar of
 *  the whole done, a spinner and a clock the page keeps ticking, since a pose of a rounded part is
 *  seconds with nothing said.  Judged, the line counts what they came to — for a few seconds when
 *  every one holds, and to stay when one does not, which the program panel then lists at its
 *  statement (`showClaims`). */
let judgingTimer = 0;
let judgingTick = 0;
let listedVerdicts: Judging['verdicts'] | null = null;
function showJudging(j: Judging | null): void {
  // the panel's list changes when the verdicts do, not at every word of the judging
  if (j?.verdicts !== listedVerdicts) {
    listedVerdicts = j?.verdicts;
    showClaims(j?.verdicts ?? []);
  }
  sayJudging(j);
}

/** A spinner's frames: work going on never looks like work that has stopped. */
const SPIN = '◐◓◑◒';
function sayJudging(j: Judging | null): void {
  clearTimeout(judgingTimer);
  clearInterval(judgingTick);
  judgingTick = 0;
  if (!j) { judgingLine(''); return; }
  if (j.error) { judgingLine(`claims: not judged — ${j.error}`, 'failed'); return; }
  if (j.verdicts) {
    const count = (vs: string[]): number =>
      j.verdicts!.filter((v) => vs.includes(v.verdict)).length;
    const held = count(['holds', 'sampled-success']);
    const [refuted, undecided] = [count(['refuted']), count(['undecided'])];
    const said = [refuted && `${refuted} refuted`, undecided && `${undecided} undecided`,
      held && `${held} hold${held === 1 ? 's' : ''}`].filter(Boolean).join(', ');
    if (refuted || undecided) { judgingLine(`claims: ${said}`, 'failed'); return; }
    judgingLine(`claims: ${said}`, 'finished');
    judgingTimer = window.setTimeout(() => judgingLine(''), 5000);
    return;
  }
  const time = clock(performance.now() - j.since);
  const n = Math.round(Math.max(0, Math.min(1, j.progress?.fraction ?? 0)) * 8);
  const spin = SPIN[Math.floor(performance.now() / 250) % SPIN.length];
  const doing = j.progress?.doing
    ?? `reading the drawing to judge ${j.claims.length} claim${j.claims.length === 1 ? '' : 's'}`;
  judgingLine(`${spin} ${doing} ${'▰'.repeat(n)}${'▱'.repeat(8 - n)} ${time}`);
  judgingTick = window.setInterval(() => sayJudging(view.claims.judging), 250);
}

/** **The load scenarios, said in the footer** after each solve: how many requirements hold and
 *  how many do not — a claim refuted, a driving row not reached, one with no answer — with each
 *  sentence the core said of them (`` scenario `shelf`: safety(bracket) is 1.22695e-05: claimed
 *  >= 1, refuted ``) as the line's tooltip.  All holding, the line stays a few seconds; one
 *  failing, or a word of the scenario's (a structure left free, a stress unbounded), it stays,
 *  and in red. */
let scenarioTimer = 0;
function showScenarios(ss: Scenario[], measures: ScenarioAnswer[] = []): void {
  clearTimeout(scenarioTimer);
  // a mass or a volume the drawing states is said beside the scenarios, and is none of theirs
  const answers = [...ss.flatMap((s) => s.answers), ...measures];
  const words = ss.flatMap((s) => s.words);
  if (!answers.length && !words.length) { scenarioLine(''); return; }
  const held = answers.filter((a) => a.holds === true).length;
  const failed = answers.filter((a) => a.holds === false).length;
  const unanswered = answers.filter((a) => a.holds === null).length;
  const said = [failed && `${failed} fail${failed === 1 ? 's' : ''}`,
    unanswered && `${unanswered} without an answer`,
    words.length && `${words.length} warning${words.length === 1 ? '' : 's'}`,
    held && `${held} hold${held === 1 ? 's' : ''}`].filter(Boolean).join(', ');
  const all = [...words, ...answers.map((a) => a.text)].join('\n');
  const label = !ss.length ? 'measures'
    : ss.length === 1 && !measures.length ? `scenario ${ss[0].name}` : 'scenarios';
  const fails = failed + unanswered + words.length > 0;
  scenarioLine(`${label}: ${said}`, fails ? 'failed' : 'finished', all);
  if (!fails) scenarioTimer = window.setTimeout(() => scenarioLine(''), 5000);
}

/** **The objects being built, said by the bar along the workspace's bottom** (#672): a row of its
 *  list clicked selects its object, as the outline's does. */
const buildBar = new BuildBar(document.getElementById('build-bar') as HTMLElement, (row) => {
  const pick = view.solidPick(row.objects[0] ?? row.solid);
  if (pick) view.pickSolid(pick, false);
  view.onSelect();
  view.draw();
});

/** Several binary STLs as one: the header of the first, the summed count, and every record. */
function joinStl(parts: Uint8Array[]): Uint8Array {
  const n = parts.reduce((a, p) => a + (new DataView(p.buffer, p.byteOffset).getUint32(80, true)), 0);
  const out = new Uint8Array(84 + n * 50);
  out.set(parts[0].subarray(0, 84));
  new DataView(out.buffer).setUint32(80, n, true);
  let at = 84;
  for (const p of parts) {
    out.set(p.subarray(84), at);
    at += p.length - 84;
  }
  return out;
}

/** Put a picture behind the drawing to trace over.
 *
 *  It is **view state, not document state** — scenery, like the camera and the colouring, and
 *  the same in kind as the paper you would tape to a drawing board.  So it is not saved, not
 *  exported, not solved and not undone: undoing a line must not move your photograph, and the
 *  Solvent document stays what somebody wrote.  What survives tracing is the drawing. */
async function traceImage(): Promise<void> {
  const got = await openImage();
  if (!got) return;
  view.traceImage(got.image, got.name, got.url);
}

/** The traced picture's two keys, live only while it is the selected thing — the brackets a
 *  drawing program fades a reference layer with.  The gate is here and not in `fadeImage`,
 *  which fades whatever picture there is: it is about what the *accelerator* may do, so it
 *  belongs beside the accelerator, in the one table every key in the app is read off. */
const fade = (by: number) => () => { if (view.underlay?.picked) view.fadeImage(by); };

const REPO = 'https://github.com/bmander/geomsolver';
const PRIMER = `${REPO}/blob/main/docs/solvent-primer.md`;

/** Follow an outbound link in its own tab, because following it leaves the sketch behind. */
const visit = (href: string) => { window.open(href, '_blank', 'noopener'); };

/* **View ▸ Show** (#678): each kind of thing the workspace draws, with its key — the kind, the
 * item, its accelerator and what it says. */
const SHOWN: [ShownKind, string, string, string][] = [
  ['objects', 'Objects', '⇧o', 'The objects, whole — not only their surfaces (⇧⌘B)'],
  ['planes', 'Planes', '⇧p', 'Each plane\u2019s pane and frame'],
  ['axes', 'Axes', '⇧a', 'The axes in space'],
  ['sketches', 'Sketches', '⇧s', 'What is drawn on planes and in space: lines, circles, arcs, '
     + 'curves and their points'],
  ['points', 'Points', '⇧t', 'The points'],
  ['fixed', 'Fixed marks', '⇧k', 'The red mark on a fixed point — off, it is drawn as any other'],
  ['dimensions', 'Dimensions', '⇧d', 'The dimension callouts — editing one still shows it'],
];

/* Everything that is neither a tool nor a constraint.  Like the constraints bar these are
 * tables rather than calls, so the accelerator printed beside an item is the same string the
 * keyboard handler matches — see ACTION_KEYS. */
const MENUS: [string, (MenuItem | string | null)[]][] = [
  ['File', [
    { label: 'New', onClick: () => view.newDocument() },
    { label: 'Open…', onClick: () => void doOpen() },
    { label: 'Open drawing folder…',
      onClick: () => void openDrawing().then((opened) => { if (opened) leaveExample(); }) },
    { label: 'Open GitHub project…',
      onClick: () => void openGithub(),
      title: 'The Solvent files of a GitHub repository, or of a folder in one, as the '
        + 'project\u2019s file tree.  A private one asks for a token' },
    { label: 'Open examples…', onClick: () => void openCase() },
    null,
    { label: 'Save', onClick: () => {
      if (!saveProjectFile()) download('sketch.json', io.dumps(view.sketch));
    } },
    { label: 'Export SVG', onClick: exportSvg,
      title: 'The drawing as a scalable image, laid out by the core — the same figure `solventc '
        + '--output` writes' },
    { label: 'Export solid (glTF)', onClick: () => void exportSolid('glb'),
      title: 'The object as a viewer opens it: every face a named node, in metres.  What '
        + '`solventc --gltf` writes' },
    { label: 'Export solid (STL)', onClick: () => void exportSolid('stl'),
      title: 'The object as a printer takes it: triangles, welded so every edge has its '
        + 'partner, from the drawing\'s own mesh' },
    { label: 'Export solid (STEP)', onClick: () => exactExport('step'),
      title: 'The exact solid, built by the core\'s own kernel and held to 10 µm: what '
        + '`solventc --step --tolerance` writes' },
    { label: 'Export solid for fabrication (STL)', onClick: () => exactExport('stl'),
      title: 'The exact solid meshed within 10 µm and held to its material field: what '
        + '`solventc --stl --tolerance` writes' },
    null,
    { label: 'Trace image…', onClick: () => void traceImage(),
      title: 'Put a picture behind the drawing to draw over.  It is scenery — not saved, not '
        + 'exported, not undone' },
    { label: 'Remove image', onClick: () => view.removeImage(),
      title: 'Take the traced picture away again' },
    { label: 'Fade image', key: '[', onClick: fade(-0.1),
      title: 'Fade the traced picture, while it is the selected thing' },
    { label: 'Brighten image', key: ']', onClick: fade(0.1),
      title: 'Bring the traced picture back up, while it is the selected thing' },
  ]],
  ['Edit', [
    { label: 'Undo', key: '⌘z', onClick: () => view.undo() },
    { label: 'Redo', key: '⇧⌘z', onClick: () => view.redo() },
    null,
    { label: 'Cut', key: '⌘x', onClick: () => report(view.cutSelected(), 'cut'),
      title: 'Take the selection out of the sketch and onto the clipboard' },
    { label: 'Copy', key: '⌘c', onClick: () => report(view.copySelected(), 'copied'),
      title: 'The selection, the points that define it, and every constraint that stays inside' },
    { label: 'Paste', key: '⌘v', onClick: () => report(view.pasteClipboard(), 'pasted'),
      title: 'A copy of the clipboard, nudged clear and selected, joined to nothing' },
    { label: 'Delete', key: 'del', onClick: () => deleteSelection(),
      title: 'Take away what is selected — the focused constraint, else the drawing\u2019s '
           + 'selection, the solids or the picture' },
    null,
    { label: 'Fit to screen', onClick: () => view.fit() },
    { label: 'Isolate', onClick: () => view.isolate(),
      title: 'Hide everything but the selection and what made it — I in the model outline' },
    { label: 'Show all', key: '⇧i', onClick: () => view.showAll(),
      title: 'Show everything the model outline hid' },
    { label: 'Program', key: '⌘p', onClick: () => toggleProgramPanel(),
      title: 'The program this drawing is written as — edit it and the drawing follows' },
    { label: 'Re-place dimensions', onClick: () => {
      // the focused dimension if there is one, otherwise every dimension on the drawing
      const n = view.resetCallouts(currentConstraint);
      toast(n ? `${n} dimension(s) put back` : 'no dimension has been moved');
    } },
  ]],
  ['View', [
    'Show',
    ...SHOWN.map(([kind, label, key, title]): MenuItem => ({
      label, key, title, checked: () => view.kindOn(kind),
      onClick: () => view.setKind(kind, !view.kindOn(kind)),
    })),
    { label: 'Inside parts', checked: () => !view.closeParts,
      onClick: () => view.setCloseParts(!view.closeParts),
      title: 'Draw what every part draws to make its object — its planes, axes, sketches and '
           + 'dimensions.  Off, an assembly shows its parts, and a double-click opens one' },
    null,
    { label: 'Close part', onClick: () => { if (!view.closePart()) toast('no part is open'); },
      title: 'Close the part a double-click opened: its insides go and the assembly is drawn '
           + 'whole again — Esc, or a click on nothing' },
    { label: 'Show solid', key: '⇧⌘b', checked: () => view.showSolid,
      onClick: () => view.setShowSolid(!view.showSolid),
      title: 'Fill each object\u2019s surfaces, not only its edges — off, a solid is a wireframe' },
    { label: 'Tangent edges', checked: () => view.showTangentEdges,
      onClick: () => view.setShowTangentEdges(!view.showTangentEdges),
      title: 'Ink, lighter, where one face of an object runs smoothly into the next (a round\u2019s '
           + 'run-out) — off, only the sharp edges' },
    { label: 'Section', key: '⇧x', checked: () => !!view.section,
      onClick: () => view.toggleSection(),
      title: 'Cut every object by the plane being drawn on, the side its normal points to taken '
           + 'away and the cut face hatched — move it, flip it or choose another plane in the '
           + 'bar it opens' },
  ]],
  ['Insert', [
    { label: 'Plane…', onClick: () => void insertPlane(),
      title: 'A named plane, constrained to the selection as the Plane button does' },
    { label: 'Component…', onClick: () => void insertComponent(),
      title: 'The selection as a named component, as the Component button makes one' },
  ]],
  ['Solution', [
    { label: 'Solve', onClick: () => { view.solveNow(); reportSolve(); } },
    { label: 'Diagnose…', onClick: () => void showDiagnosis() },
    null,
    { label: 'Animate DOF', onClick: () => {
      if (!view.startAnimation()) toast('no remaining internal DOF to animate');
    } },
    { label: 'Flip branch', onClick: () => flipBranch() },
    { label: 'Alternatives…', onClick: () => void alternatives() },
    null,
    { label: 'Options…', onClick: () => void options() },
  ]],
  ['Help', [
    { label: 'Solvent language primer', onClick: () => visit(PRIMER),
      title: 'The language a drawing is written in, as the implementation accepts it' },
    { label: 'GitHub project', onClick: () => visit(REPO),
      title: 'The source, the specification and the issue tracker' },
  ]],
];
for (const [label, items] of MENUS) {
  // The file menu and Help work in either view. Geometry commands need a selected model file.
  const shared = new Set(['New', 'Open…', 'Open drawing folder…', 'Open GitHub project…',
    'Open examples…',
    'Save', 'Export SVG', 'Program', 'Solvent language primer', 'GitHub project']);
  for (const item of items) {
    if (!item || typeof item === 'string' || shared.has(item.label)) continue;
    const action = item.onClick;
    item.onClick = () => {
      if (drawingActive()) { toast('Select a .sv file to work on its geometry'); return; }
      action();
    };
  }
  addMenu(menubar, label, items);
}
aboutBadge.addEventListener('click', () => void about());

/* -- keyboard ------------------------------------------------------------------- */

/** What the Delete key and Edit ▸ Delete both do: take away whatever is selected — the constraint
 *  with the focus, else the drawing's selection, the solids or the picture.  One function, so a
 *  phone's menu item and a keyboard's key cannot come to delete different things. */
function deleteSelection(): void {
  if (currentConstraint) {
    const c = currentConstraint;
    focusConstraint(null);
    view.removeConstraint(c);
    toast(`removed ${c.typeName}`);
  } else {
    view.deleteSelected();
  }
}

const TOOL_KEYS: Record<string, Tool> =
  Object.fromEntries(TOOLS.map(([, tool, key]) => [key, tool]));
/** Every accelerator in the app, read off the buttons and menu items themselves so there is
 *  one list and not two.  The token is the chip the control prints, lowercased: '⇧l', '⌘z'. */
const ACTION_KEYS = new Map<string, () => void>(
  [...TOOL_BUTTONS, ...VIEW_BUTTONS, ...CONSTRAINT_BUTTONS, ...MENUS.flatMap(([, items]) => items)]
    .flatMap((b) => (b && typeof b !== 'string' && b.key
      ? [[b.key, b.onClick] as [string, () => void]] : [])));

window.addEventListener('keydown', (e) => {
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
  if (drawingActive()) {
    if (e.key === 'Escape') closeMenus();
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'p') {
      e.preventDefault(); toggleProgramPanel();
    }
    return;
  }
  if (e.metaKey || e.ctrlKey || e.altKey) {
    // ⌘C and ⌘X belong to the page while there is text selected in it: taking those would stop
    // anyone copying a constraint out of the list
    const text = window.getSelection();
    const takingText = !!text && !text.isCollapsed && (e.key === 'c' || e.key === 'x');
    const cmd = e.altKey || takingText ? undefined
              : ACTION_KEYS.get(`${e.shiftKey ? '⇧' : ''}⌘${e.key.toLowerCase()}`);
    if (cmd) { e.preventDefault(); cmd(); }
    return;
  }
  const k = e.key.toLowerCase();
  if (k === 'escape') { if (!closeChooser() && !closeMenus()) view.cancelTool(); return; }
  // the curve tools collect as many points as the user wants, and a Boolean as many bodies, so
  // they need a way to say "that is all" — the only tools whose click count is not known
  if (k === 'enter') { e.preventDefault(); view.finishCurve(); return; }
  // Space lets everything go — the focused constraint too, which `onSelect` drops — and is
  // taken, so a button with the focus is not pressed by it nor the page scrolled
  if (k === ' ') { e.preventDefault(); view.selectNone(); return; }
  if (k === 'delete' || k === 'backspace') {
    e.preventDefault();
    deleteSelection();
    return;
  }
  // shift is part of the token, so ⇧L is Perpendicular and never the Line tool.  The key is
  // taken: an action that opens a dialog has focused its text field by the time the browser
  // would insert the character, and would find a stray letter in it
  const action = ACTION_KEYS.get(e.shiftKey ? `⇧${k}` : k);
  if (action) { e.preventDefault(); action(); return; }
  // and so is a tool's: an edge tool taken up over a selection asks its number at once, and the
  // letter would land in it
  const tool = TOOL_KEYS[e.shiftKey ? `⇧${k}` : k];
  if (tool) { e.preventDefault(); view.setTool(tool); }
});

/* -- boot ------------------------------------------------------------------------- */

/* A canvas press takes the selection over from the constraint that had the focus — and one
 * that picked nothing shuts the constraints window, which is the only thing that does. */
/* The model outline (#435): the document's instances as a tree, one of the panel's panes. */
const outline = new OutlinePane(document.getElementById('outline-tree')!, view,
  (row) => { if (row.span) goToSource(row.span); },
  document.getElementById('outline-isolate')!, document.getElementById('outline-show-all')!);
onSide((side) => outline.show(side === 'outline'));
// a right-click's ways to what it was on: its statement, and its row in the outline
onWays(goToSource, (row) => {
  openSide('outline');
  outline.showRow(row);
});
view.onHidden = () => outline.paint();
view.onSelect = () => {
  if (currentConstraint) focusConstraint(null);
  if (!view.selected.length) closePanel();
  showStatementFor();
  outline.reveal();
};
/* A dimension on the drawing and its row in the window are the same constraint, so clicking
 * either does the same thing — and double-clicking either opens the same number.  One clicked
 * on the drawing takes no selection with it, so it opens the window on what it holds. */
view.onPickConstraint = (c) => {
  focusConstraint(c);
  openPanel(view.highlight);
  refresh();
  view.draw();
};
view.onEditConstraint = (c) => editValue(c);

/* -- the plane being drawn on ------------------------------------------------- */

/** The chooser follows the view: None — drawing in space, its value `''` — then the planes on
 *  offer, and the one being drawn on.  Rebuilt only when the list itself changed, so a repaint
 *  does not close it under the pointer. */
function refreshPlanes(): void {
  const names = ['', ...view.planeChoices()];
  const current = view.planeName;
  if (!names.includes(current)) names.push(current);
  const shown = [...planeSelect.options].map((o) => o.value);
  if (shown.length !== names.length || names.some((n, i) => shown[i] !== n)) {
    planeSelect.replaceChildren(...names.map((n) => new Option(n || 'None', n)));
  }
  planeSelect.value = current;
}
planeSelect.addEventListener('change', () => {
  if (planeSelect.value) view.choosePlane(planeSelect.value);
  else view.drawInSpace();
  planeSelect.blur();              // the keys are the drawing's again: a tool letter, Escape
});
view.onDimension = onDimension;
view.onExtent = onExtent;
view.onRound = onRound;
view.onChoose = onChoose;
view.onContextMenu = onContextMenu;
// the section's plane, offset and side, while the workspace is cut
const sectionBar = new SectionBar(document.getElementById('section-bar') as HTMLElement, view);
view.onSection = () => sectionBar.refresh();
sectionBar.refresh();
view.onChanged = () => {
  refresh(); refreshProgram(); refreshPlanes(); outline.refresh(); sectionBar.refresh();
};
view.onPicked = refreshPanel;
// the source changed without the drawing's structure doing so — a drag wrote its seeds back, or a
// number was spliced.  Never per frame: `onDragFrame` is the frame seam and this is not wired to it
view.onProgram = () => { refreshProgram(); outline.refresh(); };
view.onLoad = () => { if (resetProgramFiles()) leaveExample(); };
view.onDragFrame = refreshStatus;
const timeline = new Timeline(document.getElementById('timeline') as HTMLElement, view);
// a constraint reading the parameter says the value it has at the instant shown (`t = 72°`)
view.onTimeline = () => { timeline.refresh(); refresh(); };
timeline.refresh();
view.onStatus = toast;
view.onBuilds = (rows) => { buildBar.update(rows); outline.paint(); };
// what `tools/open-bench.mjs` asks of the page once an opening has settled: where the objects'
// builds stand, and what the renderer drew
(globalThis as { gcsBench?: unknown }).gcsBench = {
  builds: () => view.builds.rows,
  counts: () => view.box3d.counts(),
};
view.onClaims = showJudging;
view.onScenarios = (ss, ms) => { showScenarios(ss, ms); refreshScenarioCard(view); };
view.onScenarioView = () => refreshScenarioCard(view);
bindScenarioCard(view);
refreshScenarioCard(view);
hooks.focusChanged = showStatementFor;
bindProgramPanel();
new ResizeObserver(() => view.resize()).observe(canvas);
view.resize();
view.afterEdit();
view.fit();
window.addEventListener('popstate', () => {
  const url = new URL(location.href);
  const example = url.searchParams.get('example');
  const github = url.searchParams.get('github');
  if (example) void openExample(example, 'none', url.searchParams.get('file') ?? undefined);
  else if (github) void openGithubAddress(github, 'none');
  else view.newDocument();
});
// the bare address is a blank sheet: `newDocument` leaves the example, which puts the modal away
if (initialExample) {
  await openExample(initialExample, 'replace', new URL(location.href).searchParams.get('file') ?? undefined);
}
else if (initialGithub) await openGithubAddress(initialGithub, 'replace');
else view.newDocument();
